import { API_ENDPOINTS } from '../types';
import { networkManager } from './NetworkManager';

export interface AIResponse {
  content: string;
  type: 'text' | 'code' | 'table' | 'list' | 'mixed';
}

class AIChatService {

  async generateResponse(userMessage: string): Promise<AIResponse> {
    try {
      // Get organization ID from the current user's token
      const orgId = networkManager.getOrganizationId();
      if (!orgId) {
        throw new Error('No organization ID found. Please log in again.');
      }

      // Use the NetworkManager to make the request
      const response = await networkManager.makeRequest<any>(API_ENDPOINTS.AI_CHAT.ASK(orgId), {
        method: 'POST',
        body: JSON.stringify({
          question: userMessage
        })
      });

      // Check if the response was successful
      if (!response.success) {
        throw new Error(response.message || 'Failed to get response from AI service');
      }

      // The response from Clanker is now in response.data
      const clankerResponse = response.data;
      
      // Handle different response formats from Clanker
      let content: string;
      if (typeof clankerResponse === 'string') {
        // If it's a plain string response
        content = clankerResponse;
      } else if (typeof clankerResponse === 'object' && clankerResponse !== null) {
        // If it's an object, try to extract the content
        content = clankerResponse.answer || clankerResponse.response || clankerResponse.content || JSON.stringify(clankerResponse);
      } else {
        content = 'Sorry, I could not process your request.';
      }

      return {
        content,
        type: 'text'
      };
    } catch (error: any) {
      console.error('Error calling AI API:', error);
      
      // Extract error message from JSON string
      let extractedMessage = 'An unexpected error occurred.';

      if (error instanceof Error && typeof error.message === 'string') {
        try {
          const errorData = JSON.parse(error.message);
          if (errorData.message && typeof errorData.message === 'string') {
            // If it's the specific Clanker URL error, return the info message
            if (errorData.message.includes('HasClankerURL') || errorData.message.includes('clanker URL is not set')) {
              return {
                content: 'There is no Clanker configured for this organization. Please contact the administrator to set up the AI service.',
                type: 'text'
              };
            }
            // Otherwise, use the extracted message
            extractedMessage = errorData.message;
          } else {
            // If JSON parsed but no 'message' field, use the full JSON string as fallback
            extractedMessage = error.message;
          }
        } catch (parseError) {
          // If not JSON, use the original error message
          extractedMessage = error.message;
        }
      }
      // Always re-throw the error with the extracted message
      throw new Error(extractedMessage);
    }
  }


}

export const aiChatService = new AIChatService();
